import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  Gauge,
  Users,
  Palette,
  KeyRound,
  LogOut,
  ArrowUpRight,
  Boxes,
  ShoppingBag,
  Receipt,
  FileText,
  LifeBuoy,
  ShieldCheck,
  Radio,
  GraduationCap,
  Newspaper,
  Star,
  Ban,
  Ticket,
  TrendingUp,
  Layers,
  Share2,
  Landmark,
  Rocket,
  Scale,
  Menu,
  X,
} from "lucide-react";
import Logo from "@/components/brand/Logo";
import SupportPill from "@/components/layout/SupportPill";
import { useAuth } from "@/context/AuthContext";

const nav = [
  { to: "/admin", label: "Overview", icon: Gauge, end: true },
  { to: "/admin/categories", label: "Categories", icon: Boxes },
  { to: "/admin/products", label: "Products", icon: ShoppingBag },
  { to: "/admin/bundles", label: "Bundles", icon: Layers },
  { to: "/admin/orders", label: "Orders", icon: Receipt },
  { to: "/admin/finance", label: "Finance", icon: TrendingUp },
  { to: "/admin/licenses", label: "Licenses", icon: ShieldCheck },
  { to: "/admin/signals", label: "Signals", icon: Radio },
  { to: "/admin/courses", label: "Courses", icon: GraduationCap },
  { to: "/admin/insights", label: "Insights", icon: Newspaper },
  { to: "/admin/reviews", label: "Reviews", icon: Star },
  { to: "/admin/coupons", label: "Coupons", icon: Ticket },
  { to: "/admin/affiliates", label: "Affiliates", icon: Share2 },
  { to: "/admin/brokers", label: "Brokers", icon: Landmark },
  { to: "/admin/pages", label: "Pages", icon: FileText },
  { to: "/admin/support", label: "Support", icon: LifeBuoy },
  { to: "/admin/blocklist", label: "Block list", icon: Ban },
  { to: "/admin/users", label: "Users", icon: Users },
  { to: "/admin/branding", label: "Branding", icon: Palette },
  { to: "/admin/legal", label: "Legal", icon: Scale },
  { to: "/admin/launch", label: "Launch", icon: Rocket },
  { to: "/admin/api-keys", label: "API keys", icon: KeyRound },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <div className="min-h-screen surface-abyss text-white" data-testid="admin-shell">
      <div className="grid min-h-screen lg:grid-cols-[260px_1fr]">
        <aside className="sticky top-0 hidden h-screen flex-col overflow-hidden border-r border-emerald-500/10 surface-deep lg:flex">
          <div className="shrink-0 p-6">
            <Logo />
          </div>
          <nav className="admin-sidebar-scroll flex-1 overflow-y-auto grid content-start gap-1 px-3 pb-4">
            {nav.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                data-testid={`admin-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                    isActive
                      ? "bg-emerald-500/12 text-lime-200"
                      : "text-emerald-100/60 hover:bg-white/[0.04] hover:text-white"
                  }`
                }
              >
                <n.icon size={16} />
                {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="shrink-0 border-t border-emerald-500/10 p-4">
            <div className="mb-3 rounded-lg border border-emerald-500/12 bg-[#0d2416]/60 p-3">
              <p className="overline">Signed in as</p>
              <p className="mt-1 truncate text-sm text-white">{user?.email}</p>
            </div>
            <div className="grid gap-2">
              <button onClick={() => navigate("/")} className="btn-edge-ghost w-full justify-center text-xs">
                <ArrowUpRight size={14} /> Storefront
              </button>
              <button
                onClick={() => { logout(); navigate("/"); }}
                data-testid="admin-logout"
                className="btn-edge-ghost w-full justify-center text-xs"
              >
                <LogOut size={14} /> Log out
              </button>
            </div>
          </div>
        </aside>

        <div className="min-w-0">
          {/* Mobile top bar */}
          <div className="flex items-center justify-between gap-3 border-b border-emerald-500/10 surface-deep px-4 py-3 sm:px-5 sm:py-4 lg:hidden">
            <button onClick={() => setMenuOpen(true)} data-testid="admin-menu-toggle" aria-label="Open menu"
              className="rounded-lg border border-emerald-500/20 p-2 text-emerald-100/80 hover:border-lime-400/50 hover:text-white"><Menu size={18} /></button>
            <Logo />
            <button onClick={() => { logout(); navigate("/"); }} data-testid="admin-logout-mobile" aria-label="Sign out"
              className="rounded-lg border border-emerald-500/20 p-2 text-emerald-100/80 hover:border-lime-400/50 hover:text-white"><LogOut size={16} /></button>
          </div>

          {/* Mobile drawer */}
          {menuOpen && (
            <div className="fixed inset-0 z-[70] lg:hidden" data-testid="admin-mobile-drawer">
              <div className="absolute inset-0 bg-black/60" onClick={() => setMenuOpen(false)} />
              <div className="absolute left-0 top-0 flex h-full w-72 max-w-[82%] flex-col border-r border-emerald-500/15 surface-deep">
                <div className="flex items-center justify-between p-5">
                  <Logo />
                  <button onClick={() => setMenuOpen(false)} data-testid="admin-menu-close" className="rounded-full p-1.5 text-emerald-100/60 hover:text-white"><X size={18} /></button>
                </div>
                <nav className="grid flex-1 content-start gap-1 overflow-y-auto px-3 pb-4">
                  {nav.map((n) => (
                    <NavLink key={n.to} to={n.to} end={n.end} onClick={() => setMenuOpen(false)}
                      data-testid={`admin-mnav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                      className={({ isActive }) =>
                        `flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                          isActive ? "bg-emerald-500/12 text-lime-200" : "text-emerald-100/60 hover:bg-white/[0.04] hover:text-white"
                        }`}>
                      <n.icon size={16} /> {n.label}
                    </NavLink>
                  ))}
                </nav>
                <div className="border-t border-emerald-500/10 p-4">
                  <button onClick={() => { setMenuOpen(false); navigate("/"); }} className="btn-edge-ghost w-full justify-center text-xs"><ArrowUpRight size={14} /> Storefront</button>
                </div>
              </div>
            </div>
          )}

          <main className="p-4 sm:p-6 lg:p-10">
            <Outlet />
          </main>
        </div>
      </div>
      <SupportPill />
    </div>
  );
}
